import { useEffect, useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import { adminApi, Publisher } from '../../utils/api'
import { Building, CheckCircle, XCircle, Package, Calendar } from 'lucide-react'

export default function AdminPublishersPage() {
  const { token } = useAuth()
  const [publishers, setPublishers] = useState<Publisher[]>([])
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState<string | null>(null)

  useEffect(() => {
    loadPublishers()
  }, [token])

  async function loadPublishers() {
    if (!token) return
    setLoading(true)
    console.log('[AdminPublishers] Loading publishers')
    try {
      const response = await adminApi.getPublishers(token)
      setPublishers(response.items)
    } catch (err) {
      console.error('[AdminPublishers] Failed to load:', err)
      setPublishers([])
    } finally {
      setLoading(false)
    }
  }

  async function toggleVerified(publisherId: string, currentlyVerified: boolean) {
    if (!token) return
    setActionLoading(publisherId)
    console.log('[AdminPublishers] Toggling verified status:', publisherId)
    try {
      await adminApi.updatePublisher(token, publisherId, { verified: !currentlyVerified })
      await loadPublishers()
    } catch (err) {
      console.error('[AdminPublishers] Failed to update:', err)
    } finally {
      setActionLoading(null)
    }
  }

  return (
    <div className="animate-fade-in">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Publishers</h1>
        <p className="text-gray-500 mt-1">Manage publisher accounts and verification</p>
      </div>

      <div className="card overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="mt-4 text-gray-500">Loading publishers...</p>
          </div>
        ) : publishers.length === 0 ? (
          <div className="p-8 text-center">
            <Building className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No publishers found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Publisher</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Slug</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Packages</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Joined</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {publishers.map((pub) => (
                  <tr key={pub.publisher_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                          <Building className="w-5 h-5 text-gray-400" />
                        </div>
                        <span className="font-medium text-gray-900">{pub.display_name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      @{pub.slug}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center space-x-1">
                        <Package className="w-4 h-4" />
                        <span>{pub.package_count || 0}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center space-x-1">
                        <Calendar className="w-4 h-4" />
                        <span>{new Date(pub.created_at).toLocaleDateString()}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {pub.verified ? (
                        <span className="badge badge-verified">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Verified
                        </span>
                      ) : (
                        <span className="badge bg-gray-100 text-gray-600">
                          <XCircle className="w-3 h-3 mr-1" />
                          Unverified
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <button
                        onClick={() => toggleVerified(pub.publisher_id, pub.verified)}
                        disabled={actionLoading === pub.publisher_id}
                        className={`btn text-xs px-3 py-1 ${
                          pub.verified
                            ? 'text-red-600 border border-red-200 hover:bg-red-50'
                            : 'btn-primary'
                        }`}
                      >
                        {pub.verified ? 'Remove Verification' : 'Verify'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
